/** Platform & browser detection.
 *
 *  UA parsing is unreliable as a security boundary but acceptable for
 *  diagnostics. We only need to:
 *  - tell iOS apart from everything else (including iPad-as-Mac since iPadOS 13)
 *  - extract the iOS version number for the wake-lock support gate (>=18.4)
 *  - detect installed-PWA mode (Safari sets `navigator.standalone`)
 */

interface IosStandaloneNavigator extends Navigator {
  standalone?: boolean;
}

/** iPadOS 13+ reports as MacIntel; differentiate via touch points. */
export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  const isMacTouch =
    navigator.platform === 'MacIntel' && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
  return isMacTouch;
}

/** Best-effort iOS major.minor parse from the UA string.
 *  Returns null on non-iOS or when the version block isn't present. */
export function parseIosVersion(): { major: number; minor: number; patch: number; raw: string } | null {
  if (!isIos()) return null;
  const ua = navigator.userAgent;
  // Match "OS 17_4_1" / "CPU iPhone OS 18_4 like Mac OS X" / "Version/18.4 Mobile/..."
  const m = /OS (\d+)[_.](\d+)(?:[_.](\d+))?/.exec(ua) ?? /Version\/(\d+)\.(\d+)(?:\.(\d+))?/.exec(ua);
  if (!m) return null;
  const major = Number(m[1]);
  const minor = Number(m[2]);
  const patch = m[3] ? Number(m[3]) : 0;
  if (Number.isNaN(major) || Number.isNaN(minor)) return null;
  return { major, minor, patch, raw: `${major}.${minor}${patch ? `.${patch}` : ''}` };
}

/** True if the document is rendered in standalone-PWA mode. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = navigator as IosStandaloneNavigator;
  if (nav.standalone === true) return true;
  if (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  return false;
}

/** iOS 18.4 is the minimum OS that supports Wake Lock from an installed PWA.
 *  See research doc Section 1 / WebKit bug 254545. */
export function meetsWakeLockMinIos(): boolean {
  const v = parseIosVersion();
  if (!v) return true; // non-iOS — gate doesn't apply
  if (v.major > 18) return true;
  if (v.major === 18 && v.minor >= 4) return true;
  return false;
}

/** Network Information API typings — not in lib.dom.d.ts on all TS versions. */
export interface NetworkConnection {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  type?: string;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkConnection;
  mozConnection?: NetworkConnection;
  webkitConnection?: NetworkConnection;
}

export function networkConnection(): NetworkConnection | null {
  if (typeof navigator === 'undefined') return null;
  const n = navigator as NavigatorWithConnection;
  return n.connection ?? n.mozConnection ?? n.webkitConnection ?? null;
}
